import type { GoOfflineConfig, ResolvedGoOfflineConfig } from "../types";
import { createLogger } from "./logger";
import { normalizeConfig } from "../utils/normalize-config";
import { serializeRuntimeConfig } from "../utils/serialize-config";
import { supportsServiceWorkers } from "./supports";

export interface ServiceWorkerRegistrationResult {
  registration: ServiceWorkerRegistration | null;
  config: ResolvedGoOfflineConfig;
}

function postConfig(registration: ServiceWorkerRegistration, config: ResolvedGoOfflineConfig): void {
  const payload = serializeRuntimeConfig(config);

  registration.active?.postMessage(payload);
  registration.waiting?.postMessage(payload);
  registration.installing?.postMessage(payload);
}

export async function registerServiceWorker(
  configInput: GoOfflineConfig | ResolvedGoOfflineConfig = {}
): Promise<ServiceWorkerRegistrationResult> {
  const config = normalizeConfig(configInput as GoOfflineConfig);
  const logger = createLogger(config.debug);

  if (!config.enabled) {
    logger.debug("Offline support disabled.");
    return { registration: null, config };
  }

  if (!supportsServiceWorkers()) {
    logger.warn("Service workers are not supported in this browser.");
    return { registration: null, config };
  }

  try {
    const registration = await navigator.serviceWorker.register(config.swPath, {
      scope: config.scope
    });

    logger.debug("Service worker registered.", registration);
    config.callbacks.onReady?.(registration);

    registration.addEventListener("updatefound", () => {
      const installingWorker = registration.installing;
      if (!installingWorker) {
        return;
      }

      installingWorker.addEventListener("statechange", () => {
        if (installingWorker.state === "installed") {
          if (navigator.serviceWorker.controller) {
            config.callbacks.onUpdateAvailable?.(registration);
          } else {
            config.callbacks.onInstalled?.(registration);
          }
        }
      });
    });

    if (navigator.serviceWorker.controller) {
      postConfig(registration, config);
    } else {
      navigator.serviceWorker.ready.then((readyRegistration) => postConfig(readyRegistration, config)).catch(() => {
        logger.debug("Unable to post runtime config to service worker.");
      });
    }

    return { registration, config };
  } catch (error) {
    logger.error("Service worker registration failed.", error);
    config.callbacks.onError?.(error);
    return { registration: null, config };
  }
}

